import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Calendar, Clock, Users, CheckCircle, ArrowRight } from "lucide-react";
import { trainingCourses } from "../data/mock";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Training = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    preferredDate: "",
    message: "",
  });

  const handleBookingClick = (course) => {
    setSelectedCourse(course);
    setFormData({ ...formData, course: course.title });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/training/booking`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setIsDialogOpen(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          course: "",
          preferredDate: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Training enquiry error:", error);
      toast.error("Failed to submit enquiry. Please try again or call us directly.");
    }
  };

  return (
    <section id="training" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-[#0056c5] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Medical Training
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Professional Training Courses
          </h2>
          <p className="text-xl text-gray-600">
            High-quality, hands-on training delivered by experienced professionals. Qualsafe Approved Centre (908474)
          </p>
        </div>

        {/* Training Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {trainingCourses.map((course) => (
            <Card
              key={course.id}
              className="border-2 hover:border-[#0056c5] transition-all duration-300 hover:shadow-xl relative overflow-hidden group"
            >
              {/* Decorative Element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#0056c5]/5 rounded-bl-full group-hover:scale-110 transition-transform"></div>

              <CardHeader>
                <div className="mb-2">
                  <CardTitle className="text-xl text-gray-900">
                    {course.title}
                  </CardTitle>
                </div>
                <CardDescription className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {course.duration}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <p className="text-gray-700">{course.description}</p>

                {/* Topics Covered */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">What You'll Learn:</h4>
                  <ul className="space-y-2">
                    {course.topics.slice(0, 4).map((topic, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-[#0056c5] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Availability Badge */}
                {course.available && (
                  <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                    <Users className="h-4 w-4" />
                    Places Available
                  </div>
                )}

                {/* Pricing Info */}
                <div className="text-center py-3 px-4 bg-[#0056c5]/5 rounded-lg">
                  <p className="text-sm text-gray-600 font-medium">Contact us for pricing and availability</p>
                </div>

                {/* Book Button */}
                <Button
                  onClick={() => handleBookingClick(course)}
                  className="w-full bg-[#0056c5] hover:bg-[#004399] text-white group/btn"
                >
                  Enquire About This Course
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Key Information Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-[#0056c5]/5 border-[#0056c5]/20">
            <CardHeader>
              <Calendar className="h-8 w-8 text-[#0056c5] mb-2" />
              <CardTitle className="text-lg">Flexible Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">Multiple course dates available across Surrey and Sussex locations</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0056c5]/5 border-[#0056c5]/20">
            <CardHeader>
              <CheckCircle className="h-8 w-8 text-[#0056c5] mb-2" />
              <CardTitle className="text-lg">Certified Training</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">Qualsafe approved courses with HSE compliant certification</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0056c5]/5 border-[#0056c5]/20">
            <CardHeader>
              <Users className="h-8 w-8 text-[#0056c5] mb-2" />
              <CardTitle className="text-lg">Expert Instructors</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">Experienced trainers with frontline NHS backgrounds</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Course Enquiry</DialogTitle>
            <DialogDescription>
              Fill in your details and we'll contact you with pricing and availability for {selectedCourse?.title}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferredDate">Preferred Date</Label>
              <Input
                id="preferredDate"
                type="date"
                value={formData.preferredDate}
                onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Additional Information</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any special requirements or questions..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0056c5] hover:bg-[#004399]"
              >
                Submit Enquiry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Training;
